import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  InputAdornment,
  styled,
  Typography
} from '@mui/material'
import InputBase from 'design-system/input/base'
import InputPassword from 'design-system/input/password'
import React, { useState } from 'react'
import SelectBox from 'components/SelectBox'
import EmailIcon from '@mui/icons-material/Email'
import ButtonBase from 'design-system/button'
import { InputMask } from 'components/InputMask'
import { brazilStates } from 'utils/countryState'
import { PROFESSIONALS } from 'navigation/CONSTANTS'
import useNewProfessional from './useNewProfessionals'
import NavLinkBack from 'components/NavLinkBack'
import { docMasks } from 'utils/masks'
import ConfirmationModal from 'components/ConfirmationModal'
import PermissionByRole from 'components/rules/PermissionByRole'
import { IProssionalRoleTypeEnum } from 'types/user/enum'

const StyledBox = styled(Box)(() => ({
  maxWidth: '1200px',
  padding: '20px 10% 90px 10%',
  justifyContent: 'center',
  alignSelf: 'center'
}))

const StyledTitle = styled(Typography)(() => ({
  fontSize: '14pt',
  color: '#4d4d4d'
}))

const NewProfessionalPage: React.FC = () => {
  const {
    onSubmit,
    user,
    change,
    searchProfessional,
    handleKeyDown,
    editMode,
    state,
    countryStateController,
    errorsMap,
    disableFields,
    isAdmin,
    setIsAdmin,
    buttonLabel,
    disableAdminFlag,
    enableButton,
    professionalId,
    deletionState,
    handleDeleteProfessional
  } = useNewProfessional()

  const [openDeletionModal, setOpenDeletionModal] = useState(false)

  return (
    <StyledBox>
      <Grid container>
        <Grid size={12} paddingBlock={2}>
          <NavLinkBack to={`/${PROFESSIONALS}`} title='Colaboradores' />
        </Grid>
      </Grid>
      <Typography variant='h5' padding={'20px 0px'}>
        {!editMode ? 'Cadastro de colaborador' : 'Alterar colaborador'}
      </Typography>
      <Grid container justifyContent={'center'} spacing={4}>
        {!editMode && (
          <Grid size={12}>
            <Alert variant='outlined' color='info' severity='info'>
              Primeiro digite o email para encontrar o profissional
            </Alert>
          </Grid>
        )}
        <Grid spacing={2} container flex={1}>
          <InputBase
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position='start'>
                    <EmailIcon />
                  </InputAdornment>
                ),
                autoComplete: 'off'
              }
            }}
            placeholder='exemplo@email.com'
            label='E-mail'
            variant='outlined'
            autoComplete='new-email'
            value={user.email}
            onChange={e => change('email', e.target.value)}
            onBlur={searchProfessional}
            onKeyDown={handleKeyDown}
            disabled={editMode || state == 'loading'}
            error={errorsMap.has('email')}
            helperText={errorsMap.get('email')}
          />

          {!editMode && !disableFields && (
            <InputPassword
              label='Senha'
              type='password'
              variant='outlined'
              placeholder='******'
              autoComplete='new-password'
              value={user.password}
              onChange={e => change('password', e.target.value)}
              error={errorsMap.has('password')}
              helperText={errorsMap.get('password')}
              name='new_password'
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={isAdmin}
                onChange={(_, checked) => setIsAdmin(checked)}
              />
            }
            disabled={disableAdminFlag}
            label='Administrador'
          />
        </Grid>
        <Grid spacing={2} container flexDirection={'row'}>
          <Grid size={12}>
            <StyledTitle>Dados pessoais</StyledTitle>
          </Grid>

          <Grid size={12}>
            <Grid size={{ md: 3, sm: 4, xs: 12 }}>
              <SelectBox
                label='Tratamento'
                required
                disabled={disableFields}
                menuItems={[
                  { label: 'Dr', value: 'Dr' },
                  { label: 'Dra', value: 'Dra' },
                  { label: 'Sr', value: 'Sr' },
                  { label: 'Sra', value: 'Sra' }
                ]}
                control={[user.treatment, value => change('treatment', value)]}
              />
            </Grid>
          </Grid>
          <Grid size={{ sm: 6, xs: 12 }}>
            <InputBase
              label='Nome'
              required
              variant='outlined'
              value={user.first_name}
              disabled={disableFields}
              onChange={e => change('first_name', e.target.value)}
              error={errorsMap.has('first_name')}
              helperText={errorsMap.get('first_name')}
            />
          </Grid>
          <Grid size={{ sm: 6, xs: 12 }}>
            <InputBase
              label='Sobrenome'
              required
              variant='outlined'
              value={user.last_name}
              onChange={e => change('last_name', e.target.value)}
              disabled={disableFields}
              error={errorsMap.has('last_name')}
              helperText={errorsMap.get('last_name')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <SelectBox
              label='Tipo de documento'
              required
              disabled={disableFields}
              menuItems={[
                { label: 'CRM', value: 'CRM' },
                { label: 'COREN', value: 'COREN' },
                { label: 'CREFITO', value: 'CREFITO' },
                { label: 'NA', value: 'NA' }
              ]}
              control={[
                user.professional_license,
                value => change('professional_license', value)
              ]}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3, md: 2 }}>
            {user.professional_license && user.professional_license != 'NA' && (
              <SelectBox
                label='Estado'
                required={user.professional_license != 'NA'}
                disabled={user.professional_license == 'NA' || disableFields}
                control={countryStateController}
                menuItems={brazilStates.map(item => ({
                  label: item.acronym,
                  value: item.acronym
                }))}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 5, md: 7 }}>
            {user.professional_license && user.professional_license != 'NA' && (
              <InputMask
                mask={docMasks[user.professional_license ?? ''].mask}
                label={`Digite seu ${user.professional_license}`}
                required={user.professional_license != 'NA'}
                value={user.professional_license_number}
                toUpperCase
                onChange={(_, value) =>
                  change('professional_license_number', value)
                }
                disabled={user.professional_license == 'NA' || disableFields}
                error={errorsMap.has('professional_license_number')}
                helperText={errorsMap.get('professional_license_number')}
              />
            )}
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <InputBase
              label='Nome da especialização'
              variant='outlined'
              value={user.specialization_name}
              onChange={e => change('specialization_name', e.target.value)}
              disabled={disableFields}
              error={errorsMap.has('specialization_name')}
              helperText={errorsMap.get('specialization_name')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputMask
              mask='00000'
              label='Número da especialização'
              value={user.specialization_number}
              onChange={(_, value) => change('specialization_number', value)}
              disabled={disableFields}
              error={errorsMap.has('specialization_number')}
              helperText={errorsMap.get('specialization_number')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputMask
              mask='(00) 00000-0000'
              value={user.mobile_phone}
              label='Telefone'
              required
              placeholder='(99) 99999-9999'
              onChange={(_, value) => {
                change('mobile_phone', value)
              }}
              disabled={disableFields}
              error={errorsMap.has('mobile_phone')}
              helperText={errorsMap.get('mobile_phone')}
            />
          </Grid>
        </Grid>
        <Grid size={12} justifyContent={'end'} container>
          {editMode && professionalId && (
            <PermissionByRole
              rolesAllowed={[IProssionalRoleTypeEnum.administrator]}
            >
              <ButtonBase
                color='error'
                type='button'
                sx={{ mt: 2, mr: 2 }}
                disabled={state === 'loading' || deletionState === 'loading'}
                loading={deletionState === 'loading'}
                onClick={() => setOpenDeletionModal(true)}
              >
                Remover colaborador
              </ButtonBase>
            </PermissionByRole>
          )}
          <ButtonBase
            variant='contained'
            type='submit'
            sx={{ mt: 2 }}
            disabled={!enableButton}
            loading={state == 'loading'}
            onClick={onSubmit}
          >
            {buttonLabel}
          </ButtonBase>
        </Grid>
      </Grid>
      {editMode && (
        <PermissionByRole
          rolesAllowed={[IProssionalRoleTypeEnum.administrator]}
        >
          <ConfirmationModal
            open={openDeletionModal}
            title='Deseja remover o colaborador da organização?'
            onConfirm={handleDeleteProfessional}
            onCancel={() => {
              if (deletionState !== 'loading') {
                setOpenDeletionModal(false)
              }
            }}
          />
        </PermissionByRole>
      )}
    </StyledBox>
  )
}

export default NewProfessionalPage
